<template>
  <div class="card">
    <div class="card-header py-2">
      Contour
    </div>
    <div class="card-body p-2">
      <div style="display:flex; gap: 10px; align-items: center; flex-wrap: wrap;">
        <div style="display:flex; gap:8px; align-items:center;">
          <div style="display:flex; align-items:center; gap:6px;">
            <div
              :id="'contour-line-color'"
              :style="{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: contourColorRgba, cursor: 'pointer', border: '2px solid #ccc' }"
            ></div>
            <b-popover :target="'contour-line-color'" triggers="focus" placement="top">
              <template v-slot:title>Line color</template>
              <SketchPicker v-model="contourColor" :disableAlpha="false"></SketchPicker>
            </b-popover>
          </div>
          <div style="display:flex; align-items:center; gap:6px;">
            <div
              :id="'contour-label-color'"
              :style="{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: labelColorRgba, cursor: 'pointer', border: '2px solid #ccc' }"
            ></div>
            <b-popover :target="'contour-label-color'" triggers="focus" placement="top">
              <template v-slot:title>Label color</template>
              <SketchPicker v-model="labelColor" :disableAlpha="false"></SketchPicker>
            </b-popover>
          </div>
        </div>

        <div>
          Font size:
          <select v-model.number="labelFontSize" style="margin-left: 8px;">
            <option v-for="s in fontSizes" :key="s" :value="s">{{ s }}px</option>
          </select>
        </div>

        <div>
          <button @click="reset" class="btn btn-sm btn-outline-secondary" style="padding: 2px 8px; font-size: 12px;">Reset</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BaseComponent from '@/components/Base.vue';
import { defineComponent } from 'vue';
import { NMTContourLayer } from '@/ts/NMTContourLayer';
import { EventBus } from '@/ts/EventBus';
import { error as debugError } from '@/ts/debug';

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      contourColor: { r: 0, g: 0, b: 0, a: 1 },
      labelColor: { r: 255, g: 0, b: 0, a: 1 },
      labelFontSize: 12,
      fontSizes: [10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24],
      overlayHandler: null as any,
    };
  },
  computed: {
    labelColorRgba(): string {
      const color: any = this.labelColor;
      if (typeof color === 'string') return color;
      if (color && color.rgba && typeof color.rgba === 'object') {
        const { r, g, b, a } = color.rgba;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      if (color && color.r !== undefined) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
      }
      return 'rgba(255,0,0,1)';
    }
    ,
    contourColorRgba(): string {
      const color: any = this.contourColor;
      if (typeof color === 'string') return color;
      if (color && color.rgba && typeof color.rgba === 'object') {
        const { r, g, b, a } = color.rgba;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      if (color && color.r !== undefined) {
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
      }
      return 'rgba(0,0,0,1)';
    }
  },
  methods: {
    normalizeColor(color: any) {
      if (color && color.rgba) return color.rgba;
      if (color && color.r !== undefined) return color;
      return { r: 255, g: 0, b: 0, a: 1 };
    },

    saveToLocalStorage() {
      const stored = {
        contourColor: this.normalizeColor(this.contourColor),
        labelColor: this.normalizeColor(this.labelColor),
        labelFontSize: this.labelFontSize,
      };
      localStorage.setItem('contourLabelSettings', JSON.stringify(stored));
    },

    loadFromLocalStorage() {
      try {
        const raw = localStorage.getItem('contourLabelSettings');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.contourColor) this.contourColor = parsed.contourColor;
          if (parsed.labelColor) this.labelColor = parsed.labelColor;
          if (parsed.labelFontSize) this.labelFontSize = parsed.labelFontSize;
        }
      } catch (e) {
        debugError('Failed to load contour settings', e);
      }
    },

    updateContourLayer(): boolean {
      const colorString = this.labelColorRgba;
      const contourString = this.contourColorRgba;
      const fontSizeStr = `${this.labelFontSize}px`;
      let updatedAny = false;
      try {
        const overlays = (window as any).overlayLayers;
        if (overlays && Object.keys(overlays).length > 0) {
          for (const key in overlays) {
            const layer = overlays[key];
            if (!layer) continue;
            const isContour = (typeof NMTContourLayer !== 'undefined' && layer instanceof NMTContourLayer) || (layer.constructor && layer.constructor.name === 'NMTContourLayer');
            if (isContour) {
              if (!layer.myoptions) layer.myoptions = {};
              layer.myoptions.labelColor = colorString;
              layer.myoptions.labelFontSize = fontSizeStr;
              layer.myoptions.contourColor = contourString;
              if (typeof layer.redraw === 'function') {
                try { layer.redraw(); } catch {}
              } else if (layer._map && typeof layer._map.invalidateSize === 'function') {
                try { layer._map.invalidateSize({ pan: false, animate: false }); } catch {}
              }
              updatedAny = true;
            }
          }
        }
      } catch (e) {
        debugError('Error updating contour layer', e);
      }
      return updatedAny;
    },

    reset() {
      this.labelColor = { r: 255, g: 0, b: 0, a: 1 };
      this.labelFontSize = 12;
      localStorage.removeItem('contourLabelSettings');
      this.updateContourLayer();
    }
  },
  watch: {
    labelColor: {
      handler() {
        this.saveToLocalStorage();
        this.updateContourLayer();
      },
      deep: true,
    },
    contourColor: {
      handler() {
        this.saveToLocalStorage();
        this.updateContourLayer();
      },
      deep: true,
    },
    labelFontSize() {
      this.saveToLocalStorage();
      this.updateContourLayer();
    }
  },
  mounted() {
    this.loadFromLocalStorage();
    // Try apply immediately in case overlayLayers already exist
    this.updateContourLayer();
    // Register for event when overlayLayers are ready
    this.overlayHandler = () => { this.updateContourLayer(); };
    EventBus.$on('overlayLayersReady', this.overlayHandler);
  },
  unmounted() {
    if (this.overlayHandler) {
      EventBus.$off('overlayLayersReady', this.overlayHandler);
    }
  }
});
</script>
